import * as XLSX from 'xlsx';

interface ExcelValidationResult {
  isValid: boolean;
  message: string;
}

/**
 * Validates Excel file according to specific requirements:
 * - File size must be less than 1MB
 * - Only one worksheet per Excel file (for .xls/.xlsx)
 * - No images in the Excel file (for .xls/.xlsx)
 * - CSV files are validated for size only
 */
const validateExcelFile = async (file: File): Promise<ExcelValidationResult> => {
  // Check file size (200KB = 200 * 1024 bytes)
  const maxSizeBytes = 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      message: `File size must be less than 1MB. Current size: ${Math.round(file.size / 1024)}KB`
    };
  }

  const fileName = file.name.toLowerCase();
  
  // For CSV files, only validate size (no worksheets or images to check)
  if (fileName.endsWith('.csv')) {
    return {
      isValid: true,
      message: 'CSV file validation passed'
    };
  }

  // For Excel files (.xls/.xlsx), perform additional validations
  try {
    // Read the file as array buffer
    const arrayBuffer = await file.arrayBuffer();
    
    // Parse the Excel file
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    
    // Check number of worksheets
    const sheetNames = workbook.SheetNames;
    if (sheetNames.length > 1) {
      return {
        isValid: false,
        message: `Excel file must contain only one worksheet. Found ${sheetNames.length} worksheets: ${sheetNames.join(', ')}`
      };
    }

    // Check for images/media in the Excel file
    // For .xlsx files, we can check the internal structure
    if (fileName.endsWith('.xlsx')) {
      // Check if the raw file contains media content patterns
      const uint8Array = new Uint8Array(arrayBuffer);
      const fileContent = new TextDecoder('utf-8', { fatal: false }).decode(uint8Array);
      
      // Look for common image file signatures and media-related XML content
      const hasImageContent = 
        fileContent.includes('xl/media/') ||
        fileContent.includes('<drawing') ||
        fileContent.includes('image') ||
        fileContent.includes('picture') ||
        fileContent.includes('media') ||
        // Check for common image file headers in binary data
        arrayBuffer.byteLength > 0 && (
          // PNG signature
          new Uint8Array(arrayBuffer).some((_, i) => 
            i <= arrayBuffer.byteLength - 8 &&
            new Uint8Array(arrayBuffer, i, 8).toString() === '137,80,78,71,13,10,26,10'
          ) ||
          // JPEG signature
          new Uint8Array(arrayBuffer).some((_, i) => 
            i <= arrayBuffer.byteLength - 4 &&
            new Uint8Array(arrayBuffer, i, 4).toString() === '255,216,255,224'
          ) ||
          // GIF signature
          new Uint8Array(arrayBuffer).some((_, i) => 
            i <= arrayBuffer.byteLength - 6 &&
            new TextDecoder().decode(new Uint8Array(arrayBuffer, i, 6)) === 'GIF87a'
          )
        );
      
      if (hasImageContent) {
        return {
          isValid: false,
          message: 'Excel file must not contain any images or media content'
        };
      }
    }

    // Additional check using XLSX library for drawing objects
    const worksheet = workbook.Sheets[sheetNames[0]];
    if (worksheet && (worksheet['!drawings'] || worksheet['!images'])) {
      return {
        isValid: false,
        message: 'Excel file must not contain any images or drawing objects'
      };
    }

    return {
      isValid: true,
      message: 'Excel file validation passed'
    };

  } catch (error) {
    return {
      isValid: false,
      message: 'Failed to validate Excel file. Please ensure the file is not corrupted.'
    };
  }
};

export default validateExcelFile;